<template>
  <div
    ref="websiteSearchApp"
    class="w-full flex flex-col relative">
    <div
      v-if="state.searchInputType === SIDEBAR_SEARCH_INPUT_TYPE"
      class="w-full flex flex-col">
      <div class="w-full h-8 relative">
        <input
          ref="searchInput"
          v-model="state.searchValue"
          type="search"
          name="q"
          :placeholder="state.isPartNumberSearch ? 'Enter part number' : 'Smart Search'"
          class="sidebar-search-input w-full h-8 border border-black py-2 pr-11 pl-5 text-sm placeholder:text-sm text-light-gray placeholder:text-light-gray"
          :style="sidebarSearchInputStyle"
          @input="searchInputHandler"
          @focus="partNumberSearchFocusHandler"
          @click="partNumberSearchFocusHandler"
          @keyup.enter="searchRequestHandler" />
        <div
          class="w-7 h-7 rounded-full bg-base-color hover:bg-dark-base-color border-none absolute top-1/2 -translate-y-1/2 right-0.5 cursor-pointer flex items-center justify-center tr-all"
          @click="searchRequestHandler">
          <img
            src="/images/icons/icon-search-white.svg"
            alt="Search icon"
            width="16"
            height="20"
            class="block w-4 h-auto" />
        </div>
      </div>
      <div
        v-if="isPartSearchEnabled"
        class="w-full flex items-start justify-end mt-1">
        <TrueFalseLabelsSwitcherComponent
          v-model:value="state.isPartNumberSearch"
          falseLabel="Smart Search"
          trueLabel="Part Search"
          textColor="text-gray" />
      </div>
    </div>

    <div
      v-if="state.searchInputType === PAGE_CONTENT_SEARCH_INPUT_TYPE"
      class="product-search-v1 w-full max-w-md flex flex-col">
      <div class="w-full flex flex-col items-end relative overflow-hidden bg-white rounded-[20px]">
        <input
          ref="searchInput"
          v-model="state.searchValue"
          type="text"
          name="Smart search"
          class="page-content-search-input w-full h-10 bg-white border-none outline-none pl-5 pr-32 text-gray italic font-medium leading-normal"
          :placeholder="state.isPartNumberSearch ? 'Enter part number' : 'Smart search'"
          @input="searchInputHandler"
          @focus="partNumberSearchFocusHandler"
          @click="partNumberSearchFocusHandler"
          @keyup.enter="searchRequestHandler" />
        <div
          class="w-28 h-8 rounded-full absolute right-1 top-1 flex items-center justify-center cursor-pointer bg-base-color hover:bg-dark-base-color tr-all"
          @click="searchRequestHandler">
          <img
            src="/images/icons/icon-search-white.svg"
            alt="Icon search white"
            width="16"
            height="20"
            class="block w-4 h-auto mr-2" />
          <span class="font-medium text-white">Search</span>
        </div>
        <div
          v-if="isPartSearchEnabled"
          class="w-full flex justify-end px-4 py-1">
          <TrueFalseLabelsSwitcherComponent
            v-model:value="state.isPartNumberSearch"
            falseLabel="Smart Search"
            trueLabel="Part Search"
            textColor="text-gray" />
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="isPartNumberSearchResultsVisible"
        class="bg-white border border-light-gray rounded-t-none rounded-b-lg overflow-hidden shadow-md fixed"
        :class="{
          'z-[1030]': state.searchInputType === SIDEBAR_SEARCH_INPUT_TYPE,
          'z-[999]': state.searchInputType === PAGE_CONTENT_SEARCH_INPUT_TYPE,
        }"
        :style="partNumberSearchResultsStyle">
        <div
          class="overflow-y-auto"
          :style="{ maxHeight: partNumberSearchResultsStyle.maxHeight }">
          <div
            v-if="state.isPartNumberSearchIndexLoading"
            class="w-full px-4 py-3 flex items-center justify-center gap-2 text-sm text-gray"
            role="status">
            <div class="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
            <span>Loading part numbers...</span>
          </div>
          <div
            v-if="state.partNumberSearchResults.length > 0"
            class="w-full px-4 pt-2 pb-1 flex items-center justify-between gap-2 text-xs font-medium text-light-gray">
            <span>{{ partNumberSearchOptions.length > 0 ? 'Continue search' : 'Search results' }}</span>
            <button
              type="button"
              aria-label="Hide part number suggestions"
              class="border-none bg-transparent p-0 text-xl leading-none text-light-gray hover:text-base-color cursor-pointer tr-all"
              @click="clearPartNumberSearch">
              &times;
            </button>
          </div>
          <button
            v-for="result in partNumberSearchOptions"
            :key="result.part_number"
            type="button"
            class="block w-full border-none bg-transparent px-4 py-2 text-left text-sm text-gray hover:text-base-color hover:bg-gray-100 cursor-pointer tr-all"
            @click="completePartNumberSearch(result.part_number)">
            {{ result.part_number }}
          </button>
          <div
            v-if="partNumberSearchOptions.length > 0 && partNumberViewOptions.length > 0"
            class="w-full border-t border-light-gray" />
          <div
            v-if="partNumberViewOptions.length > 0"
            class="w-full px-4 pt-2 pb-1 text-xs font-medium text-light-gray">
            View product page
          </div>
          <a
            v-for="result in partNumberViewOptions"
            :key="`${result.url}-${result.part_number}`"
            :href="result.url"
            target="_blank"
            rel="noopener noreferrer"
            class="block w-full px-4 py-2 text-sm text-gray hover:text-base-color hover:bg-gray-100 tr-all">
            {{ result.part_number }}
          </a>
          <div
            v-if="!state.isPartNumberSearchIndexLoading && state.partNumberSearchResults.length === 0"
            class="w-full px-4 py-2 flex items-center justify-between gap-2 text-sm text-gray">
            <button
              type="button"
              class="flex-shrink-0 border-none bg-transparent p-0 text-sm text-base-color hover:text-dark-base-color cursor-pointer tr-all"
              @click="contactUsHandler">
              Part number not found. Click here to contact us
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <div
      v-if="state.isSearchValueErrorVisible"
      class="w-full text-sm text-red">
      Please enter a minimum of
      {{ VariablesHelper.SEARCH_INPUT_MIN_LENGTH }}
      characters.
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import GlobalPreloaderHelper from '@/helpers/global/globalPreloader.helper';
import VariablesHelper from '@/helpers/global/variables.helper';
import { loadPartNumberSearchIndex } from '@/helpers/website/partNumberSearchIndex.helper';
import type { PartNumberSearchResult } from '@/helpers/website/partNumberSearchIndex.helper';
import TrueFalseLabelsSwitcherComponent from '@/components/website/form-components/TrueFalseLabelsSwitcherComponent.vue';

const SIDEBAR_SEARCH_INPUT_TYPE = 'sidebar-search-input';
const PAGE_CONTENT_SEARCH_INPUT_TYPE = 'page-content-search-input';
const PART_NUMBER_PREFIX_LENGTH = 2;
const PART_NUMBER_SEARCH_EMPTY_STATE_DELAY = 1000;
const isPartSearchEnabled = ref(Boolean(IS_PART_SEARCH_ENABLED));

interface PartNumberAutocompleteResult extends PartNumberSearchResult {
  isComplete: boolean;
}

const websiteSearchApp = ref<HTMLElement | null>(null);
const searchInput = ref<HTMLInputElement | null>(null);

const state = reactive({
  selectedCompanyUid: '',
  searchInputType: PAGE_CONTENT_SEARCH_INPUT_TYPE,
  searchValue: '',
  isPartNumberSearch: false,
  isSearchValueErrorVisible: false,
  isPartNumberSearchCompleted: false,
  isPartNumberSearchSubmitted: false,
  isPartNumberSearchIndexLoaded: false,
  isPartNumberSearchIndexLoading: false,
  partNumberSearchIndex: [] as PartNumberSearchResult[],
  partNumberSearchResults: [] as PartNumberAutocompleteResult[],
  searchUrl: '/search',
});

const partNumberSearchResultsStyle = reactive({
  top: '0px',
  left: '0px',
  width: '0px',
  maxHeight: '0px',
});

let partNumberSearchPositionContainer: HTMLElement | null = null;
let partNumberSearchPositionAnimationFrame: number | null = null;

const isPartNumberSearchResultsVisible = computed(() => (
  state.isPartNumberSearch
  && (
    state.partNumberSearchResults.length > 0
    || state.isPartNumberSearchIndexLoading
    || (state.isPartNumberSearchSubmitted && state.isPartNumberSearchCompleted)
  )
));

const sidebarSearchInputStyle = computed(() => ({
  borderRadius: isPartNumberSearchResultsVisible.value ? '16px 16px 0 0' : '16px',
}));

const partNumberSearchOptions = computed(() => (
  state.partNumberSearchResults.filter((result) => !result.isComplete)
));

const partNumberViewOptions = computed(() => (
  state.partNumberSearchResults.filter((result) => result.isComplete)
));

const updatePartNumberSearchResultsPosition = () => {
  if (searchInput.value === null) {
    return;
  }

  const inputRect = searchInput.value.getBoundingClientRect();
  const width = Math.min(inputRect.width, window.innerWidth);
  const left = Math.min(Math.max(inputRect.left, 0), window.innerWidth - width);

  partNumberSearchResultsStyle.top = `${inputRect.bottom}px`;
  partNumberSearchResultsStyle.left = `${left}px`;
  partNumberSearchResultsStyle.width = `${width}px`;
  partNumberSearchResultsStyle.maxHeight = `${Math.max(window.innerHeight - inputRect.bottom - 8, 120)}px`;
};

const trackPartNumberSearchResultsPosition = () => {
  updatePartNumberSearchResultsPosition();
  partNumberSearchPositionAnimationFrame = window.requestAnimationFrame(
    trackPartNumberSearchResultsPosition,
  );
};

const startPartNumberSearchPositionTracking = (event: TransitionEvent) => {
  if (
    event.target !== partNumberSearchPositionContainer
    || partNumberSearchPositionAnimationFrame !== null
  ) {
    return;
  }

  trackPartNumberSearchResultsPosition();
};

const stopPartNumberSearchPositionTracking = (event?: TransitionEvent) => {
  if (event !== undefined && event.target !== partNumberSearchPositionContainer) {
    return;
  }

  if (partNumberSearchPositionAnimationFrame !== null) {
    window.cancelAnimationFrame(partNumberSearchPositionAnimationFrame);
    partNumberSearchPositionAnimationFrame = null;
  }

  if (event !== undefined) {
    updatePartNumberSearchResultsPosition();
  }
};

const removePartNumberSearchPositionListeners = () => {
  window.removeEventListener('resize', updatePartNumberSearchResultsPosition);
  window.removeEventListener('scroll', updatePartNumberSearchResultsPosition, true);
  partNumberSearchPositionContainer?.removeEventListener(
    'transitionrun',
    startPartNumberSearchPositionTracking,
  );
  partNumberSearchPositionContainer?.removeEventListener(
    'transitionend',
    stopPartNumberSearchPositionTracking,
  );
  partNumberSearchPositionContainer?.removeEventListener(
    'transitioncancel',
    stopPartNumberSearchPositionTracking,
  );
  stopPartNumberSearchPositionTracking();
  partNumberSearchPositionContainer = null;
};

const updatePartNumberSearchPositionListeners = async (isResultsVisible: boolean) => {
  removePartNumberSearchPositionListeners();
  if (!isResultsVisible) {
    return;
  }

  await nextTick();
  updatePartNumberSearchResultsPosition();
  partNumberSearchPositionContainer = searchInput.value?.closest<HTMLElement>(
    '.website-sidebar, #page-content',
  ) ?? null;
  window.addEventListener('resize', updatePartNumberSearchResultsPosition);
  window.addEventListener('scroll', updatePartNumberSearchResultsPosition, true);
  partNumberSearchPositionContainer?.addEventListener(
    'transitionrun',
    startPartNumberSearchPositionTracking,
  );
  partNumberSearchPositionContainer?.addEventListener(
    'transitionend',
    stopPartNumberSearchPositionTracking,
  );
  partNumberSearchPositionContainer?.addEventListener(
    'transitioncancel',
    stopPartNumberSearchPositionTracking,
  );
};

const getInfoFromDataAttrs = () => {
  if (websiteSearchApp.value !== null) {
    const searchAppContainer = websiteSearchApp.value.parentElement;
    if (searchAppContainer === null) {
      return;
    }

    const dataAttrSearchInputType = searchAppContainer.getAttribute('data-search-input-type');
    if (
      dataAttrSearchInputType === SIDEBAR_SEARCH_INPUT_TYPE
      || dataAttrSearchInputType === PAGE_CONTENT_SEARCH_INPUT_TYPE
    ) {
      state.searchInputType = dataAttrSearchInputType;
    }

    const dataAttrSearchUrl = searchAppContainer.getAttribute('data-search-url');
    if (dataAttrSearchUrl !== null && dataAttrSearchUrl.length > 0) {
      state.searchUrl = dataAttrSearchUrl;
    }

    const dataAttrSearchQ = searchAppContainer.getAttribute('data-search-q');
    if (dataAttrSearchQ !== null && dataAttrSearchQ.length > 0) {
      state.searchValue = decodeURIComponent(dataAttrSearchQ.replace(/\+/g, ' '));
    }

    const dataAttrSearchCompanyUid = searchAppContainer.getAttribute('data-search-company-uid');
    if (dataAttrSearchCompanyUid !== null && dataAttrSearchCompanyUid.length > 0) {
      state.selectedCompanyUid = dataAttrSearchCompanyUid;
    }

    if (searchAppContainer.getAttribute('data-part-search-enabled') === 'false') {
      isPartSearchEnabled.value = false;
      state.isPartNumberSearch = false;
    }
  }
};

const normalizePartNumberSearchQuery = (query: string) => query.toUpperCase().replace(/\s+/g, '');

const searchByCompanyItemsClickHandler = () => {
  const searchByCompanyItems = document.querySelectorAll<HTMLElement>('.search-by-company-item');
  searchByCompanyItems.forEach((item) => {
    item.addEventListener('click', () => {
      const clickedCompanyUid = item.getAttribute('data-company-uid') ?? '';
      state.selectedCompanyUid = state.selectedCompanyUid === clickedCompanyUid
        ? ''
        : clickedCompanyUid;

      searchByCompanyItems.forEach((companyItem) => {
        const isSelected = companyItem.getAttribute('data-company-uid') === state.selectedCompanyUid;
        companyItem.classList.toggle('!text-base-color', isSelected);
        companyItem.classList.toggle('font-medium', isSelected);
      });

      if (state.searchValue.length >= VariablesHelper.SEARCH_INPUT_MIN_LENGTH) {
        searchRequestHandler();
      }
    });
  });
};

let partNumberSearchEmptyStateTimeout: number | null = null;

const clearPartNumberSearchEmptyStateTimeout = () => {
  if (partNumberSearchEmptyStateTimeout !== null) {
    window.clearTimeout(partNumberSearchEmptyStateTimeout);
    partNumberSearchEmptyStateTimeout = null;
  }
};

const clearPartNumberSearch = () => {
  clearPartNumberSearchEmptyStateTimeout();
  state.isPartNumberSearchCompleted = false;
  state.isPartNumberSearchSubmitted = false;
  state.partNumberSearchResults = [];
};

const updatePartNumberSearchResults = (showEmptyState = false, autoComplete = true) => {
  let query = normalizePartNumberSearchQuery(state.searchValue);
  if (query.length === 0 && state.isPartNumberSearchIndexLoaded) {
    const firstTwoLetterCombinations = new Set(
      state.partNumberSearchIndex
        .map((result) => normalizePartNumberSearchQuery(result.part_number)
          .slice(0, PART_NUMBER_PREFIX_LENGTH))
        .filter((partNumber) => partNumber.length === PART_NUMBER_PREFIX_LENGTH),
    );

    state.partNumberSearchResults = Array.from(firstTwoLetterCombinations)
      .sort((firstPartNumber, secondPartNumber) => firstPartNumber.localeCompare(secondPartNumber))
      .map((partNumber) => ({
        part_number: partNumber,
        url: '',
        isComplete: false,
      }));
    state.isPartNumberSearchCompleted = true;
    return;
  }

  if (!state.isPartNumberSearchIndexLoaded) {
    state.isPartNumberSearchCompleted = false;
    state.isPartNumberSearchSubmitted = showEmptyState;
    return;
  }

  state.isPartNumberSearchSubmitted = showEmptyState;
  const matchingPartNumbers = state.partNumberSearchIndex
    .map((result) => normalizePartNumberSearchQuery(result.part_number))
    .filter((partNumber) => partNumber.startsWith(query));

  if (autoComplete) {
    while (
      matchingPartNumbers.length > 0
      && !matchingPartNumbers.includes(query)
    ) {
      const nextCharacters = new Set(
        matchingPartNumbers.map((partNumber) => partNumber[query.length]),
      );
      if (nextCharacters.size !== 1) {
        break;
      }

      query += Array.from(nextCharacters)[0];
    }
  }

  if (query !== normalizePartNumberSearchQuery(state.searchValue)) {
    state.searchValue = query;
  }

  const exactMatches: PartNumberAutocompleteResult[] = [];
  const nextCharacterOptions = new Map<string, PartNumberSearchResult[]>();

  state.partNumberSearchIndex.forEach((result) => {
    const normalizedPartNumber = normalizePartNumberSearchQuery(result.part_number);
    if (!normalizedPartNumber.startsWith(query)) {
      return;
    }

    if (normalizedPartNumber === query) {
      exactMatches.push({ ...result, isComplete: true });
      return;
    }

    const nextCharacterOption = normalizedPartNumber.slice(0, query.length + 1);
    const optionMatches = nextCharacterOptions.get(nextCharacterOption) ?? [];
    optionMatches.push(result);
    nextCharacterOptions.set(nextCharacterOption, optionMatches);
  });

  const nextCharacterResults: PartNumberAutocompleteResult[] = [];
  Array.from(nextCharacterOptions.entries())
    .sort(([firstOption], [secondOption]) => firstOption.localeCompare(secondOption))
    .forEach(([partNumber, optionMatches]) => {
      optionMatches
        .filter((result) => normalizePartNumberSearchQuery(result.part_number) === partNumber)
        .forEach((result) => nextCharacterResults.push({ ...result, isComplete: true }));

      if (optionMatches.some(
        (result) => normalizePartNumberSearchQuery(result.part_number).length > partNumber.length,
      )) {
        nextCharacterResults.push({
          part_number: partNumber,
          url: '',
          isComplete: false,
        });
      }
    });

  state.partNumberSearchResults = [
    ...exactMatches,
    ...nextCharacterResults,
  ];
  state.isPartNumberSearchCompleted = true;
};

const completePartNumberSearch = (partNumber: string) => {
  state.searchValue = partNumber;
  searchInput.value?.focus();
};

const searchPartNumbers = async (showEmptyState = false, autoComplete = true) => {
  const query = normalizePartNumberSearchQuery(state.searchValue);
  if (!state.isPartNumberSearchIndexLoaded) {
    state.isPartNumberSearchIndexLoading = true;
    try {
      state.partNumberSearchIndex = await loadPartNumberSearchIndex();
    } catch {
      return;
    } finally {
      state.isPartNumberSearchIndexLoading = false;
    }
    state.isPartNumberSearchIndexLoaded = true;
  }

  if (
    !state.isPartNumberSearch
    || query !== normalizePartNumberSearchQuery(state.searchValue)
  ) {
    return;
  }

  updatePartNumberSearchResults(showEmptyState, autoComplete);
  if (!showEmptyState) {
    schedulePartNumberSearchEmptyState();
  }
};

const partNumberSearchFocusHandler = () => {
  if (state.isPartNumberSearch && !isPartNumberSearchResultsVisible.value) {
    searchPartNumbers();
  }
};

const schedulePartNumberSearchEmptyState = () => {
  clearPartNumberSearchEmptyStateTimeout();
  if (state.partNumberSearchResults.length > 0) {
    return;
  }

  partNumberSearchEmptyStateTimeout = window.setTimeout(() => {
    if (
      state.isPartNumberSearch
      && state.isPartNumberSearchCompleted
      && state.partNumberSearchResults.length === 0
    ) {
      state.isPartNumberSearchSubmitted = true;
    }
  }, PART_NUMBER_SEARCH_EMPTY_STATE_DELAY);
};

const contactUsHandler = () => {
  const partNumber = normalizePartNumberSearchQuery(state.searchValue);
  if (partNumber.length === 0) {
    return;
  }

  window.dispatchEvent(new CustomEvent(
    VariablesHelper.OPEN_TUTCO_IMMEDIATE_ASSISTANCE_MODAL_EVENT,
    {
      detail: {
        comments: `Hello! I couldn't find any information about part number ${partNumber} on your website. Could you please provide me with more details about this part?`,
      },
    },
  ));
};

const searchInputHandler = () => {
  if (state.isPartNumberSearch) {
    state.searchValue = state.searchValue.toUpperCase();
    state.isSearchValueErrorVisible = false;
    return;
  }

  if (
    state.isSearchValueErrorVisible
    && state.searchValue.trim().length >= VariablesHelper.SEARCH_INPUT_MIN_LENGTH
  ) {
    state.isSearchValueErrorVisible = false;
  }
};

const searchRequestHandler = () => {
  if (state.isPartNumberSearch) {
    clearPartNumberSearch();
    state.isSearchValueErrorVisible = false;
    clearPartNumberSearchEmptyStateTimeout();
    searchPartNumbers(true);
    return;
  }

  if (state.searchValue.trim().length < VariablesHelper.SEARCH_INPUT_MIN_LENGTH) {
    state.isSearchValueErrorVisible = true;
    return;
  }

  GlobalPreloaderHelper.show();
  state.isSearchValueErrorVisible = false;

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = state.searchUrl;

  const csrfField = document.createElement('input');
  csrfField.type = 'hidden';
  csrfField.name = 'csrfmiddlewaretoken';
  csrfField.value = CSRF_TOKEN;
  form.appendChild(csrfField);

  const queryField = document.createElement('input');
  queryField.type = 'hidden';
  queryField.name = 'q';
  queryField.value = `${state.searchValue.replace(' ', '+')}`;
  form.appendChild(queryField);

  if (state.selectedCompanyUid.length > 0) {
    const companyField = document.createElement('input');
    companyField.type = 'hidden';
    companyField.name = 'company';
    companyField.value = state.selectedCompanyUid;
    form.appendChild(companyField);
  }

  document.body.appendChild(form);
  history.replaceState(null, '', window.location.href);
  form.submit();
};

const clearSidebarSearch = () => {
  if (state.searchInputType !== SIDEBAR_SEARCH_INPUT_TYPE) {
    return;
  }

  state.searchValue = '';
  state.isSearchValueErrorVisible = false;
  clearPartNumberSearch();
};

onMounted(() => {
  state.searchUrl = `${window.location.href.split('search')[0]}search`;
  getInfoFromDataAttrs();
  if (state.searchInputType === PAGE_CONTENT_SEARCH_INPUT_TYPE) {
    searchByCompanyItemsClickHandler();
  }
  window.addEventListener(VariablesHelper.WEBSITE_SIDEBAR_CLOSED_EVENT, clearSidebarSearch);
});

onBeforeUnmount(() => {
  removePartNumberSearchPositionListeners();
  clearPartNumberSearchEmptyStateTimeout();
  window.removeEventListener(VariablesHelper.WEBSITE_SIDEBAR_CLOSED_EVENT, clearSidebarSearch);
});

watch(isPartNumberSearchResultsVisible, updatePartNumberSearchPositionListeners);

watch(
  () => state.searchValue,
  (searchValue, previousSearchValue) => {
    if (!state.isPartNumberSearch) {
      return;
    }

    state.isSearchValueErrorVisible = false;
    const isDeleting = normalizePartNumberSearchQuery(searchValue).length
      < normalizePartNumberSearchQuery(previousSearchValue).length;
    searchPartNumbers(false, !isDeleting);
  },
);

watch(
  () => state.isPartNumberSearch,
  () => {
    state.isSearchValueErrorVisible = false;
    if (state.isPartNumberSearch) {
      searchPartNumbers();
    } else {
      clearPartNumberSearch();
    }
  },
);
</script>
